
import { useState, useCallback } from 'react';
import { debugLogger } from '@/utils/debugLogger';

export interface ImageVariant {
  type: 0 | 1 | 2 | 3;
  file: File;
  width: number;
  height: number;
  quality: number;
  actualSize: number;
  dimensions: string;
}

export const useMultiSizeImageProcessor = () => {
  const [isProcessing, setIsProcessing] = useState(false);

  const processImageToVariants = useCallback(async (
    originalFile: File
  ): Promise<ImageVariant[]> => {
    debugLogger.debug('[useMultiSizeImageProcessor] ENTRY: Processing image to variants:', {
      originalFileName: originalFile.name,
      originalSize: originalFile.size,
      originalType: originalFile.type
    });

    setIsProcessing(true);

    try {
      // Create variants configuration - 4 optimized numeric variants (1:1 square)
      const variants = [
        { type: 0 as const, maxWidth: 80, quality: 0.85 },    // avatar
        { type: 1 as const, maxWidth: 200, quality: 0.75 },   // thumbnail
        { type: 2 as const, maxWidth: 500, quality: 0.80 },   // card
        { type: 3 as const, maxWidth: 1200, quality: 0.90 }   // display
      ];

      const processedVariants: ImageVariant[] = [];

      for (const variant of variants) {
        debugLogger.debug('[useMultiSizeImageProcessor] Processing variant:', variant.type);

        const processedFile = await processImageVariant(
          originalFile, 
          variant.maxWidth, 
          variant.quality
        );

        // Get dimensions
        const dimensions = await getImageDimensions(processedFile);

        processedVariants.push({
          type: variant.type,
          file: processedFile,
          width: dimensions.width,
          height: dimensions.height,
          quality: variant.quality,
          actualSize: processedFile.size,
          dimensions: `${dimensions.width}x${dimensions.height}`
        });

        debugLogger.debug('[useMultiSizeImageProcessor] Variant processed:', {
          type: variant.type,
          originalSize: originalFile.size,
          processedSize: processedFile.size,
          dimensions: `${dimensions.width}x${dimensions.height}`,
          compressionRatio: Math.round((1 - processedFile.size / originalFile.size) * 100)
        });
      }

      debugLogger.debug('[useMultiSizeImageProcessor] SUCCESS: All variants processed:', {
        originalSize: originalFile.size,
        totalVariants: processedVariants.length,
        totalProcessedSize: processedVariants.reduce((sum, v) => sum + v.actualSize, 0),
        variants: processedVariants.map(v => ({
          type: v.type,
          size: v.actualSize,
          dimensions: v.dimensions
        }))
      });

      return processedVariants;

    } catch (error) {
      debugLogger.error('[useMultiSizeImageProcessor] ERROR: Failed to process variants:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  return {
    isProcessing,
    processImageToVariants
  };
};

/**
 * Process single image variant
 */
const processImageVariant = async (
  file: File,
  maxWidth: number,
  quality: number
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      const { width, height } = calculateDimensions(img.width, img.height, maxWidth);
      
      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx?.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const processedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(processedFile);
          } else {
            reject(new Error('Failed to create blob'));
          }
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Calculate dimensions maintaining aspect ratio
 */
const calculateDimensions = (
  originalWidth: number, 
  originalHeight: number, 
  maxWidth: number
): { width: number; height: number } => {
  if (originalWidth <= maxWidth) {
    return { width: originalWidth, height: originalHeight };
  }

  const aspectRatio = originalHeight / originalWidth;
  return {
    width: maxWidth,
    height: Math.round(maxWidth * aspectRatio)
  };
};

/**
 * Get image dimensions from file
 */
const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => reject(new Error('Failed to load image for dimensions'));
    img.src = URL.createObjectURL(file);
  });
};
